import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { User, Eye, EyeOff, LogIn, Lock } from 'lucide-react';
import '../styles/LoginPage.css'; // Assuming you have a CSS file for styling

// Define the roles available for selection
const roles = [
    { label: 'Administrator', value: 'administrator' },
    { label: 'Wykładowca', value: 'lecturer' },
    { label: 'Kursant', value: 'student' }
];

export default function LoginPage() {
    // Initialize navigate from react-router-dom
    const navigate = useNavigate();
    // State for username input
    const [username, setUsername] = useState('');
    // State for password input
    const [password, setPassword] = useState('');
    // State for selected role
    const [selectedRole, setSelectedRole] = useState(null);
    // State for message to display
    const [message, setMessage] = useState('');
    // State for focused input field
    const [focusedInput, setFocusedInput] = useState('');
    // State for showing/hiding password
    const [showPassword, setShowPassword] = useState(false);

    // Function to handle role selection
    const handleRoleClick = (role) => {
        setSelectedRole(role);
    }

    // Function to handle login form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedRole) {
            setMessage('Przed logowaniem wybierz rolę.');
            return;
        }

        try {

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
                username,
                password,
                role: selectedRole
            });


            const user = response.data.user;
            const token = user.token;

            if (!token) {
                setMessage('Brak tokena w odpowiedzi serwera.');
                return;
            }

            // Save token and user data to localStorage
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Navigate to dashboard based on role
            switch (user.role) {
                case 'administrator':
                    navigate('/admin/dashboard');
                    break;
                case 'lecturer':
                    navigate('/lecturer/dashboard');
                    break;
                case 'student':
                    // Check if student needs to accept terms based on latest user data from backend
                    if (
                        user.termsStatus &&
                        (
                            !user.termsStatus.privacy_policy_accepted ||
                            !user.termsStatus.terms_accepted ||
                            !user.termsStatus.age_of_majority_confirmed
                        )
                    ) {
                        // Redirect to profile with settings tab active
                        navigate('/student/profile?tab=settings');
                    } else {
                        navigate('/student/dashboard');
                    }
                    break;
                default:
                    setMessage('Nieznana rola użytkownika.');
            }

        } catch (error) {
            setMessage(error.response?.data?.error || 'Wystąpił błąd podczas logowania.');
        }
    };

    // Render the login page
    return (
        <div className="login-container">
            <div className="login-card">
                {/* Logo and header */}
                <div className="logo-section">
                    <div className="logo-container">
                        <img src="/images/logo.svg" alt="Logo szkoły" className='login-logo-image' />
                    </div>
                    <h1 className="title">PSDiA</h1>
                    <p className="subtitle">Polska Szkoła Dendrologii i Arborystyki</p>
                    <p className="tagline">System zarządzania kursami</p>
                </div>
                {/* Login form */}
                <div className="login-form">
                    <form onSubmit={handleSubmit}>
                        {/* Role choose */}
                        <p className="label role-label">Wybierz rolę:</p>
                        <div className="role-selection">
                            {roles.map(({ label, value }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => handleRoleClick(value)}
                                    className={`role-button ${selectedRole === value ? 'active' : ''}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        {/* Username input */}
                        <div className="input-group">
                            <p className="label">Nazwa użytkownika:</p>
                            <div className={`input-wrapper ${focusedInput === 'username' ? 'focused' : ''}`}>
                                <User className="input-icon" size={20} />
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    onFocus={() => setFocusedInput('username')}
                                    onBlur={() => setFocusedInput('')}
                                    className="input-field"
                                    placeholder="Wprowadź nazwę użytkownika"
                                    required
                                />
                            </div>
                        </div>
                        {/* Password input */}
                        <div className="input-group">
                            <p className="label">Hasło:</p>
                            <div className={`input-wrapper ${focusedInput === 'password' ? 'focused' : ''}`}>
                                <Lock className="input-icon" size={20} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onFocus={() => setFocusedInput('password')}
                                    onBlur={() => setFocusedInput('')}
                                    className="input-field"
                                    placeholder="Wprowadź hasło"
                                    required
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>
                        {/* Submit button */}
                        <div className="button-group">
                            <button type="submit" className="submit-button">
                                <LogIn className="submit-icon" size={20} />
                                Zaloguj
                            </button>
                        </div>
                    </form>
                    {/* Error message */}
                    {message && (
                        <div className="error-message">
                            {message}
                        </div>
                    )}
                </div>
                {/* Footer */}
                <div className="footer">
                    <p className="footer-text">
                        ® 2025 System Arrio - PSDiA (Wersja 0.1.47) ·{" "}
                        <Link to="/privacy-policy" style={{ color: '#16a34a', textDecoration: 'none' }}>
                            Polityka prywatności
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
